import { useState, useEffect, useRef } from 'react';
import { VariableSummary } from '../types/stats';
import { formatCount, formatNumber, formatPercent } from '../utils/format';

interface StatTableProps {
  summary: VariableSummary;
}

const headers = [
  { 
    key: 'mean', 
    label: 'Mean', 
    tooltip: 'Average value of all measurements' 
  },
  { 
    key: 'stdev', 
    label: 'StDev', 
    tooltip: 'Standard Deviation - measure of data spread' 
  },
  { 
    key: 'coefVar', 
    label: 'CoefVar', 
    tooltip: 'Coefficient of Variation - relative variability (StDev/Mean)' 
  },
  { 
    key: 'min', 
    label: 'Min', 
    tooltip: 'Minimum value in the dataset' 
  },
  { 
    key: 'q1', 
    label: 'Q1', 
    tooltip: 'First Quartile - 25th percentile' 
  },
  { 
    key: 'median', 
    label: 'Median', 
    tooltip: 'Middle value when data is sorted (50th percentile)' 
  },
  { 
    key: 'q3', 
    label: 'Q3', 
    tooltip: 'Third Quartile - 75th percentile' 
  },
  { 
    key: 'max', 
    label: 'Max', 
    tooltip: 'Maximum value in the dataset' 
  },
  { 
    key: 'range', 
    label: 'Range', 
    tooltip: 'Difference between maximum and minimum values' 
  },
  { 
    key: 'iqr', 
    label: 'IQR', 
    tooltip: 'Interquartile Range - difference between Q3 and Q1' 
  },
] as const;

interface HeaderWithTooltipProps {
  label: string;
  tooltip: string;
  headerKey: string;
  isOpen: boolean;
  onToggle: (key: string) => void;
}

const HeaderWithTooltip = ({ label, tooltip, headerKey, isOpen, onToggle }: HeaderWithTooltipProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        popupRef.current &&
        buttonRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        onToggle(headerKey);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onToggle, headerKey]);

  return (
    <th className="py-3 pr-3 font-medium relative">
      <div className="flex items-center gap-1.5">
        <span>{label}</span>
        <button
          ref={buttonRef}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggle(headerKey);
          }}
          className={`inline-flex items-center justify-center w-4 h-4 rounded-full transition-colors ${
            isOpen 
              ? 'bg-blue-500 text-white' 
              : 'bg-slate-200 hover:bg-slate-300 text-slate-600 hover:text-slate-800'
          }`}
          aria-label={`Information about ${label}`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="w-3 h-3"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
      {isOpen && (
        <div
          ref={popupRef}
          className="absolute z-50 left-full ml-2 top-0 w-64 p-3 bg-white border border-slate-300 rounded-lg shadow-xl"
          style={{ boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}
        >
          <div className="absolute left-0 top-3 -ml-2 w-0 h-0 border-t-8 border-b-8 border-r-8 border-transparent border-r-slate-300"></div>
          <div className="absolute left-0 top-3 -ml-1.5 w-0 h-0 border-t-8 border-b-8 border-r-8 border-transparent border-r-white"></div>
          <p className="text-sm text-slate-700 leading-relaxed">{tooltip}</p>
        </div>
      )}
    </th>
  );
};

export const StatTable = ({ summary }: StatTableProps) => {
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  const totalButtonRef = useRef<HTMLButtonElement>(null);
  const totalPopupRef = useRef<HTMLDivElement>(null);

  const displayedRows = summary.rows.filter((row) => row.comment !== 'No Indication');
  const rowsToRender = displayedRows.length > 0 ? displayedRows : summary.rows;
  const totalRecords = summary.rows.reduce((total, row) => total + row.totalCount, 0);
  const measuredRecords = summary.rows.reduce((total, row) => total + row.valueCount, 0);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openTooltip === 'total') {
        if (
          totalPopupRef.current &&
          totalButtonRef.current &&
          !totalPopupRef.current.contains(event.target as Node) &&
          !totalButtonRef.current.contains(event.target as Node)
        ) {
          setOpenTooltip(null);
        }
      }
    };

    if (openTooltip === 'total') {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openTooltip]);

  const handleToggleTooltip = (key: string) => {
    setOpenTooltip(openTooltip === key ? null : key);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[700px] text-left text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-slate-500">
              <th className="py-3 pr-3 font-medium">Defect Type</th>
              <th className="py-3 pr-3 font-medium relative">
                <div className="flex items-center gap-1.5">
                  <span>Total</span>
                  <button
                    ref={totalButtonRef}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleTooltip('total');
                    }}
                    className={`inline-flex items-center justify-center w-4 h-4 rounded-full transition-colors ${
                      openTooltip === 'total'
                        ? 'bg-blue-500 text-white'
                        : 'bg-slate-200 hover:bg-slate-300 text-slate-600 hover:text-slate-800'
                    }`}
                    aria-label="Information about Total"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="w-3 h-3"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
                {openTooltip === 'total' && (
                  <div
                    ref={totalPopupRef}
                    className="absolute z-50 left-full ml-2 top-0 w-64 p-3 bg-white border border-slate-300 rounded-lg shadow-xl"
                    style={{ boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}
                  >
                    <div className="absolute left-0 top-3 -ml-2 w-0 h-0 border-t-8 border-b-8 border-r-8 border-transparent border-r-slate-300"></div>
                    <div className="absolute left-0 top-3 -ml-1.5 w-0 h-0 border-t-8 border-b-8 border-r-8 border-transparent border-r-white"></div>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      Total number of records for this defect type
                    </p>
                  </div>
                )}
              </th>
              {headers.map((header) => (
                <HeaderWithTooltip
                  key={header.key}
                  label={header.label}
                  tooltip={header.tooltip}
                  headerKey={header.key}
                  isOpen={openTooltip === header.key}
                  onToggle={handleToggleTooltip}
                />
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rowsToRender.length === 0 ? (
              <tr>
                <td colSpan={headers.length + 2} className="py-6 text-center text-slate-500">
                  No comment-level measurements provided for this variable.
                </td>
              </tr>
            ) : (
              rowsToRender.map((row) => (
                <tr key={row.comment} className="text-slate-700">
                  <td className="py-4 pr-3">
                    <div className="font-semibold text-slate-900">{row.comment}</div>
                  </td>
                  <td className="py-4 pr-3 font-semibold text-slate-900">{formatCount(row.totalCount)}</td>
                  <td className="py-4 pr-3">{formatNumber(row.mean)}</td>
                  <td className="py-4 pr-3">{formatNumber(row.stdev)}</td>
                  <td className="py-4 pr-3">{formatPercent(row.coefVar)}</td>
                  <td className="py-4 pr-3">{formatNumber(row.min)}</td>
                  <td className="py-4 pr-3">{formatNumber(row.q1)}</td>
                  <td className="py-4 pr-3">{formatNumber(row.median)}</td>
                  <td className="py-4 pr-3">{formatNumber(row.q3)}</td>
                  <td className="py-4 pr-3">{formatNumber(row.max)}</td>
                  <td className="py-4 pr-3">{formatNumber(row.range)}</td>
                  <td className="py-4 pr-3">{formatNumber(row.iqr)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
